import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { CrmEmptyState } from "@/components/crm/CrmEmptyState";
import { RecordLinkRow } from "@/components/crm/RecordLinkRow";
import { formatCrmLeadTypeLabel, formatCrmStatusLabel } from "@/lib/crmDisplay";
import {
  leadAccountTabListLabel,
  leadListAllTabPrimaryLabel,
  leadListAllTabSubtitle,
  leadListPrimaryContactName,
} from "@/lib/crmLeadListDisplay";
import {
  buildCrmLeadsListReturnQuery,
  writeCrmLeadListNav,
  type CrmLeadListNavEntry,
} from "@/lib/crmLeadListNav";
import { useCrmOrgUserDirectory } from "@/lib/crmOwnerUsers";

type LeadContact = {
  contactId?: string | null;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  title?: string | null;
  linkedinUrl?: string | null;
};

type LeadRow = {
  id: string;
  leadName?: string;
  companyName?: string | null;
  companyDomain?: string | null;
  stage?: string;
  status?: string;
  source?: string | null;
  ownerId?: string | null;
  leadType?: string | null;
  score?: number | null;
  updatedAt?: string;
  contacts?: LeadContact[];
};

type LeadView = "all" | "company" | "contacts";

function getLeadView(raw: string | null): LeadView {
  if (raw === "company" || raw === "contacts" || raw === "all") return raw;
  return "all";
}

function LeadPrimaryContactCell({
  leadId,
  contacts,
}: {
  leadId: string;
  contacts?: LeadContact[] | null;
}) {
  const name = leadListPrimaryContactName(contacts);
  if (!name) return <>—</>;
  const contactId = contacts?.[0]?.contactId;
  if (contactId) {
    const href = `/crm/leads/${encodeURIComponent(leadId)}?contact=${encodeURIComponent(contactId)}`;
    return (
      <Link
        to={href}
        className="text-indigo-600 hover:underline dark:text-indigo-300"
        data-row-ignore
      >
        {name}
      </Link>
    );
  }
  return <>{name}</>;
}

export function CrmLeadsPage() {
  const { resolveOwnerLabel } = useCrmOrgUserDirectory();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedView = getLeadView(searchParams.get("view"));
  const query = (searchParams.get("q") || "").trim().toLowerCase();
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<LeadRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<{ leads?: LeadRow[] }>("/api/crm/leads?limit=500");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load leads.");
        setLeads([]);
      } else {
        setError(null);
        setLeads(res.data?.leads ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const contacts = useMemo(
    () =>
      leads.flatMap((lead) =>
        (lead.contacts || []).map((contact, index) => ({
          leadId: lead.id,
          contactId: contact.contactId || `${lead.id}-${index}`,
          leadName: lead.leadName || "Lead",
          company: lead.companyName || "No account linked",
          fullName:
            contact.fullName ||
            [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() ||
            "Unknown",
          email: contact.email || null,
          title: contact.title || null,
          linkedinUrl: contact.linkedinUrl || null,
          status: lead.status || "open",
          updatedAt: lead.updatedAt || "",
        })),
      ),
    [leads],
  );

  const matchLead = (lead: LeadRow) =>
    !query ||
    [
      lead.leadName,
      lead.companyName,
      lead.companyDomain,
      lead.stage,
      lead.status,
      lead.source,
      lead.ownerId,
      resolveOwnerLabel(lead.ownerId),
      ...(lead.contacts || []).flatMap((c) => [c.fullName, c.firstName, c.lastName, c.email, c.linkedinUrl]),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));

  const matchContact = (contact: (typeof contacts)[number]) =>
    !query ||
    [contact.fullName, contact.company, contact.email, contact.title, contact.linkedinUrl, contact.status]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));

  const filteredLeads = leads.filter(matchLead);
  const filteredContacts = contacts.filter(matchContact);
  const activeLeads = filteredLeads.filter((l) => l.status !== "converted" && l.status !== "disqualified");
  const activeContacts = filteredContacts.filter((c) => c.status !== "converted");

  const allRows = useMemo(
    () =>
      [
        ...activeLeads.map((lead) => ({
          id: `lead-${lead.id}`,
          leadId: lead.id,
          type: formatCrmLeadTypeLabel(lead.leadType),
          primaryLabel: leadListAllTabPrimaryLabel(lead),
          subtitle: leadListAllTabSubtitle(lead),
          contacts: lead.contacts,
          stageOrStatus: `${formatCrmStatusLabel(lead.stage)} / ${formatCrmStatusLabel(lead.status)}`,
          owner: resolveOwnerLabel(lead.ownerId),
          updatedAt: lead.updatedAt || "",
        })),
      ].sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || "")),
    [activeLeads, resolveOwnerLabel],
  );

  useEffect(() => {
    if (loading) return;
    let entries: CrmLeadListNavEntry[] = [];
    if (selectedView === "all") {
      entries = allRows.map((row) => ({ leadId: row.leadId }));
    } else if (selectedView === "company") {
      entries = activeLeads.map((lead) => ({ leadId: lead.id }));
    } else {
      entries = activeContacts.map((contact) => ({
        leadId: contact.leadId,
        contact: contact.contactId,
      }));
    }
    writeCrmLeadListNav({
      entries,
      returnQuery: buildCrmLeadsListReturnQuery(searchParams),
    });
  }, [loading, selectedView, allRows, activeLeads, activeContacts, searchParams]);

  const navClass = (isActive: boolean) =>
    isActive
      ? "rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white"
      : "rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15";

  const setView = (view: LeadView) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    setSearchParams(next);
  };

  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  const querySuffix = query ? `&q=${encodeURIComponent(query)}` : "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Leads</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Track inbound opportunities, qualification status, and owner assignments.
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {activeLeads.length} open lead{activeLeads.length === 1 ? "" : "s"}
            {activeContacts.length
              ? ` · ${activeContacts.length} contact${activeContacts.length === 1 ? "" : "s"} on the Contact tab`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/crm/leads/converted"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
          >
            Converted leads
          </Link>
          <Link
            to="/crm/import?target=leads"
            className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-900 hover:bg-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-950/40 dark:text-indigo-100"
          >
            Import
          </Link>
          <Link
            to="/crm/leads/new"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
          >
            New Lead
          </Link>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <input type="hidden" name="view" value={selectedView} />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search leads, accounts, contacts, stage, or owner"
          className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <button
          type="submit"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Search
        </button>
      </form>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={navClass(selectedView === "all")} onClick={() => setView("all")}>
          All
        </button>
        <button type="button" className={navClass(selectedView === "company")} onClick={() => setView("company")}>
          Account
        </button>
        <button type="button" className={navClass(selectedView === "contacts")} onClick={() => setView("contacts")}>
          Contact
        </button>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading leads…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
          CRM leads are unavailable. {error}
        </div>
      ) : null}

      {selectedView === "all" && !loading && !error && allRows.length === 0 && !query ? (
        <CrmEmptyState
          title="No leads yet"
          description="Capture your first opportunity to start the sales motion. You can convert a lead into an account and contact, then open a deal."
          primaryCta={{ href: "/crm/leads/new", label: "New Lead" }}
          secondaryCta={{ href: "/crm/import?target=leads", label: "Import CSV" }}
        />
      ) : null}

      {selectedView === "all" && !loading && (allRows.length > 0 || query || error) ? (
        <LeadsAllTable rows={allRows} query={query} />
      ) : null}

      {selectedView === "company" && !loading && !error && activeLeads.length === 0 && !query ? (
        <CrmEmptyState
          title="No account leads yet"
          description="Add a lead account to track qualification and ownership before converting to a CRM account."
          primaryCta={{ href: "/crm/leads/new", label: "New Lead" }}
          secondaryCta={{ href: "/crm/import?target=leads", label: "Import CSV" }}
        />
      ) : null}

      {selectedView === "company" && !loading && (activeLeads.length > 0 || query || error) ? (
        <LeadsCompanyTable leads={activeLeads} query={query} resolveOwnerLabel={resolveOwnerLabel} />
      ) : null}

      {selectedView === "contacts" && !loading && !error && activeContacts.length === 0 && !query ? (
        <CrmEmptyState
          title="No lead contacts yet"
          description="Add contacts on a lead to qualify people before conversion, or create a converted contact from Accounts."
          primaryCta={{ href: "/crm/leads/new", label: "New Lead" }}
          secondaryCta={{ href: "/crm/contacts/new", label: "Create a contact" }}
        />
      ) : null}

      {selectedView === "contacts" && !loading && (activeContacts.length > 0 || query || error) ? (
        <LeadsContactsTable contacts={activeContacts} query={query} />
      ) : null}

      {/* preserve query in view links for bookmarking */}
      <span className="sr-only">{querySuffix}</span>
    </div>
  );
}

function LeadsAllTable({
  rows,
  query,
}: {
  rows: Array<{
    id: string;
    leadId: string;
    type: string;
    primaryLabel: string;
    subtitle: string | null;
    contacts?: LeadContact[] | null;
    stageOrStatus: string;
    owner: string;
    updatedAt: string;
  }>;
  query: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
          <tr>
            <th className="px-4 py-3 font-semibold">Type</th>
            <th className="px-4 py-3 font-semibold">Account</th>
            <th className="px-4 py-3 font-semibold">Contact</th>
            <th className="px-4 py-3 font-semibold">Stage / Status</th>
            <th className="px-4 py-3 font-semibold">Owner</th>
            <th className="px-4 py-3 font-semibold">Updated</th>
            <th className="px-4 py-3 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <RecordLinkRow key={row.id} href={`/crm/leads/${encodeURIComponent(row.leadId)}`}>
              <td className="px-4 py-3">{row.type}</td>
              <td className="px-4 py-3">
                <Link
                  to={`/crm/leads/${encodeURIComponent(row.leadId)}`}
                  className="font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  {row.primaryLabel}
                </Link>
                {row.subtitle ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">{row.subtitle}</div>
                ) : null}
              </td>
              <td className="px-4 py-3">
                <LeadPrimaryContactCell leadId={row.leadId} contacts={row.contacts} />
              </td>
              <td className="px-4 py-3 capitalize">{row.stageOrStatus}</td>
              <td className="px-4 py-3">{row.owner}</td>
              <td className="px-4 py-3">{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}</td>
              <td className="px-4 py-3">
                <Link
                  to={`/crm/leads/${encodeURIComponent(row.leadId)}/convert`}
                  className="rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-400/50 dark:text-indigo-200 dark:hover:bg-indigo-900/30"
                  data-row-ignore
                >
                  Convert
                </Link>
              </td>
            </RecordLinkRow>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                {query ? "No matching leads found." : "No leads yet."}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function LeadsCompanyTable({
  leads,
  query,
  resolveOwnerLabel,
}: {
  leads: LeadRow[];
  query: string;
  resolveOwnerLabel: (ownerId: string | null | undefined) => string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
          <tr>
            <th className="px-4 py-3 font-semibold">Account</th>
            <th className="px-4 py-3 font-semibold">Contact</th>
            <th className="px-4 py-3 font-semibold">Stage</th>
            <th className="px-4 py-3 font-semibold">Owner</th>
            <th className="px-4 py-3 font-semibold">Score</th>
            <th className="px-4 py-3 font-semibold">Updated</th>
            <th className="px-4 py-3 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => {
            const { primary, subtitle } = leadAccountTabListLabel(lead);
            return (
            <RecordLinkRow key={lead.id} href={`/crm/leads/${encodeURIComponent(lead.id)}`}>
              <td className="px-4 py-3">
                <Link
                  to={`/crm/leads/${encodeURIComponent(lead.id)}`}
                  className="font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  {primary}
                </Link>
                {subtitle ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
                ) : null}
              </td>
              <td className="px-4 py-3">
                <LeadPrimaryContactCell leadId={lead.id} contacts={lead.contacts} />
              </td>
              <td className="px-4 py-3 capitalize">{formatCrmStatusLabel(lead.stage)}</td>
              <td className="px-4 py-3">{resolveOwnerLabel(lead.ownerId)}</td>
              <td className="px-4 py-3">{lead.score ?? "—"}</td>
              <td className="px-4 py-3">{lead.updatedAt ? new Date(lead.updatedAt).toLocaleString() : "—"}</td>
              <td className="px-4 py-3">
                <Link
                  to={`/crm/leads/${encodeURIComponent(lead.id)}/convert`}
                  className="rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-400/50 dark:text-indigo-200 dark:hover:bg-indigo-900/30"
                  data-row-ignore
                >
                  Convert
                </Link>
              </td>
            </RecordLinkRow>
            );
          })}
          {leads.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                {query ? "No matching account leads found." : "No account leads found."}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function LeadsContactsTable({
  contacts,
  query,
}: {
  contacts: Array<{
    leadId: string;
    contactId: string;
    fullName: string;
    company: string;
    email: string | null;
    title: string | null;
    status: string;
    updatedAt: string;
  }>;
  query: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/10">
          <tr>
            <th className="px-4 py-3 font-semibold">Contact</th>
            <th className="px-4 py-3 font-semibold">Account</th>
            <th className="px-4 py-3 font-semibold">Email</th>
            <th className="px-4 py-3 font-semibold">Title</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Updated</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => {
            const href = `/crm/leads/${encodeURIComponent(contact.leadId)}?contact=${encodeURIComponent(contact.contactId)}`;
            return (
              <RecordLinkRow key={contact.contactId} href={href}>
                <td className="px-4 py-3 font-medium">
                  <Link to={href} className="text-indigo-600 hover:underline dark:text-indigo-300">
                    {contact.fullName}
                  </Link>
                </td>
                <td className="px-4 py-3">{contact.company}</td>
                <td className="px-4 py-3">{contact.email || "No email"}</td>
                <td className="px-4 py-3">{contact.title || "—"}</td>
                <td className="px-4 py-3 capitalize">{formatCrmStatusLabel(contact.status)}</td>
                <td className="px-4 py-3">{contact.updatedAt ? new Date(contact.updatedAt).toLocaleString() : "—"}</td>
              </RecordLinkRow>
            );
          })}
          {contacts.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                {query ? "No matching contact leads found." : "No contact leads found."}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
